import React, { useState, lazy, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import styles from "./App.module.scss";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import ViewportProvider from "./hoc/ViewportProvider";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LocaleProvider, useLocale, useLocaleRouteMatch } from "./i18n/LocaleContext";
import { AVAILABLE_LOCALES, DEFAULT_LOCALE, isValidLocale } from "./i18n/locales";

import SiteNav from "./containers/SiteNav/SiteNav";
import DonationModal from "../src/containers/DonationModal/DonationModal";
import AuthModal from "../src/containers/AuthModal/AuthModal";
import Footer from "../src/containers/Footer/Footer";
import { LocaleSEO } from "./components/LocaleSEO";
import { initializeAnalytics, trackPageView } from "./utilities/analytics";

// import VideoVaultPage from "./containers/VideoVaultPage/VideoVaultPage";
// import AboutPage from "./containers/AboutPage/AboutPage";
// import EinsteinDollarPage from "./containers/EinsteinDollarPage/EinsteinDollarPage";
import { routerBasename } from "./utilities/basePath";
import { useFullscreenMode } from "./utilities/fullscreenMode";
import { featureFlags } from "./utilities/featureFlags";

const LandingPage = lazy(() => import("./containers/LandingPage/LandingPage"));
const VideoVaultPage = lazy(() =>
  import("./containers/VideoVaultPage/VideoVaultPage")
);
const AboutPage = lazy(() => import("./containers/AboutPage/AboutPage"));
const StorePage = lazy(() => import("./containers/StorePage/StorePage"));
const AmpedUpGamePage = lazy(() => import("./containers/AmpedUpGamePage/AmpedUpGamePage"));
const MotionMapperPage = lazy(() => import("./containers/MotionMapperPage/MotionMapperPage"));
const CrackTheCircuitPage = lazy(() => import("./containers/CrackTheCircuitPage/CrackTheCircuitPage"));
const WavemakerPage = lazy(() => import("./containers/WavemakerPage/WavemakerPage"));
const VisualAccelerometerPage = lazy(() => import("./containers/VisualAccelerometerPage/VisualAccelerometerPage"));
const KinematicsLabPage = lazy(() => import("./containers/KinematicsLabPage/KinematicsLabPage"));
const PolarityShiftPage = lazy(() => import("./containers/PolarityShiftPage/PolarityShiftPage"));
const MagnetismMiniGolfPage = lazy(() => import("./containers/MagnetismMiniGolfPage/MagnetismMiniGolfPage"));
const PlotThePathPage = lazy(() => import("./containers/PlotThePathPage/PlotThePathPage"));
const EnergyBarChartPage = lazy(() => import("./containers/EnergyBarChartPage/EnergyBarChartPage"));
const AccountPage = lazy(() => import("./containers/AccountPage/AccountPage"));
const EmbeddedGamePage = lazy(() => import("./containers/EmbeddedGamePage/EmbeddedGamePage"));

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initializeAnalytics();
    trackPageView(`${location.pathname}${location.search}${location.hash}`);
  }, [location]);

  return null;
};

/**
 * Non-default locales that require a URL prefix (e.g., /es, /fr).
 * The default locale (en) uses paths without a prefix.
 */
const PREFIXED_LOCALES = AVAILABLE_LOCALES.filter((l) => l !== DEFAULT_LOCALE);

/**
 * Redirect /en/* to /* (strips the default locale prefix).
 * e.g., /en/about -> /about, /en -> /
 */
const DefaultLocaleRedirect = () => {
  const location = useLocation();
  const pathWithoutLocale = location.pathname.replace(
    new RegExp(`^/${DEFAULT_LOCALE}(/|$)`),
    "/"
  );
  const newPath = pathWithoutLocale === "" ? "/" : pathWithoutLocale;
  return <Navigate to={newPath + location.search + location.hash} replace />;
};

/**
 * Validates if the first path segment is an unsupported locale-like pattern.
 * If it looks like a locale but isn't supported, redirect to the path without the prefix.
 * Otherwise, render the app content.
 */
const ValidateLocaleOrRedirect = ({ children }) => {
  const location = useLocation();

  // Extract the first path segment
  const firstSegment = location.pathname.split("/")[1] || "";

  // Check if this looks like a locale code but isn't supported
  const looksLikeLocale = /^[a-z]{2}(?:-[A-Za-z]{2})?$/.test(firstSegment);

  if (looksLikeLocale && !isValidLocale(firstSegment)) {
    // It's a 2-letter code but not a supported locale - redirect without the prefix
    const pathWithoutLocale = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, "/");
    const newPath = pathWithoutLocale === "" ? "/" : pathWithoutLocale;
    return <Navigate to={newPath + location.search + location.hash} replace />;
  }

  return children;
};

const LegacyGameEmbedRedirect = ({ gameId }) => {
  const location = useLocation();
  return <Navigate to={`/${gameId}/embed${location.search}${location.hash}`} replace />;
};

// Keep bookmarked comparison links on the canonical localized game route.
const AmpedUpRedirect = () => {
  const location = useLocation();
  const { localePath } = useLocale();
  return <Navigate to={localePath("/amped-up") + location.search + location.hash} replace />;
};

const LoadingFallback = () => {
  const { t } = useLocale();
  return <div className={styles.LoadingText}>{t("common.loading")}</div>;
};

function App() {
  return (
    <HelmetProvider>
      <ViewportProvider>
        <BrowserRouter basename={routerBasename}>
          <AuthProvider>
            <LocaleProvider>
              <AnalyticsTracker />
              <Suspense fallback={<LoadingFallback />}>
                <ValidateLocaleOrRedirect>
                  <div className={styles.App}>
                    <Routes>
                      {/* Redirect /en/* to /* (strip default locale prefix) */}
                      <Route path={`/${DEFAULT_LOCALE}/*`} element={<DefaultLocaleRedirect />} />

                      {/* Legacy Motion Mapper locale routes redirect to new format */}
                      <Route path="/motion-mapper-es" element={<Navigate to="/es/motion-mapper" replace />} />
                      <Route path="/motion-mapper-fr" element={<Navigate to="/fr/motion-mapper" replace />} />

                      {/* Additional legacy redirects (to default locale paths) */}
                      <Route path="/polarityshift" element={<Navigate to="/polarity-shift" replace />} />
                      <Route path="/polarity-shift/index.html" element={<Navigate to="/polarity-shift" replace />} />
                      <Route path="/videovault" element={<Navigate to="/video-vault" replace />} />
                      <Route path="/motionmapper" element={<Navigate to="/motion-mapper" replace />} />
                      <Route path="/wavemaker/index.html" element={<Navigate to="/wavemaker" replace />} />
                      <Route
                        path="/wavemaker/embed"
                        element={<EmbeddedGamePage gameId="wavemaker" />}
                      />
                      <Route
                        path="/polarity-shift/embed"
                        element={<EmbeddedGamePage gameId="polarity-shift" />}
                      />
                      <Route
                        path="/crack-the-circuit/embed"
                        element={<EmbeddedGamePage gameId="crack-the-circuit" />}
                      />
                      <Route
                        path="/motion-mapper/embed"
                        element={<EmbeddedGamePage gameId="motion-mapper" />}
                      />
                      <Route
                        path="/kinematics-lab/embed"
                        element={<EmbeddedGamePage gameId="kinematics-lab" />}
                      />
                      <Route
                        path="/magnetism-mini-golf/embed"
                        element={<EmbeddedGamePage gameId="magnetism-mini-golf" />}
                      />
                      <Route
                        path="/wavemaker/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="wavemaker" />}
                      />
                      <Route
                        path="/polarity-shift/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="polarity-shift" />}
                      />
                      <Route
                        path="/crack-the-circuit/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="crack-the-circuit" />}
                      />
                      <Route
                        path="/motion-mapper/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="motion-mapper" />}
                      />
                      <Route
                        path="/kinematics-lab/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="kinematics-lab" />}
                      />
                      <Route
                        path="/magnetism-mini-golf/labXchangeVersion.html"
                        element={<LegacyGameEmbedRedirect gameId="magnetism-mini-golf" />}
                      />

                      {/* Non-default locale routes with prefix (es, fr, etc.) */}
                      {PREFIXED_LOCALES.map((locale) => (
                        <Route
                          key={locale}
                          path={`/${locale}/*`}
                          element={<AppChrome />}
                        />
                      ))}

                      {/* Default locale (en) routes - no prefix (must be last) */}
                      <Route path="/*" element={<AppChrome />} />
                    </Routes>
                  </div>
                </ValidateLocaleOrRedirect>
              </Suspense>
            </LocaleProvider>
          </AuthProvider>
        </BrowserRouter>
      </ViewportProvider>
    </HelmetProvider>
  );
}

const AppChrome = () => {
  const [donationModalShowing, setDonationModalShowing] = useState(false);
  const [donationSuccessAmount, setDonationSuccessAmount] = useState(null);
  const { isFullscreenRoute } = useFullscreenMode();
  const { authModalShowing, closeAuthModal, authEvent, clearAuthEvent } = useAuth();
  const { localePath } = useLocale();
  const routeMatches = useLocaleRouteMatch();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!featureFlags.donations || params.get("donation") !== "success") {
      return;
    }

    const amountParam = params.get("donation_amount");
    const parsedAmount = amountParam ? Number(amountParam) : null;
    if (Number.isFinite(parsedAmount) && parsedAmount > 0) {
      setDonationSuccessAmount(parsedAmount);
    } else {
      setDonationSuccessAmount(null);
    }

    setDonationModalShowing(true);

    params.delete("donation");
    params.delete("donation_amount");
    params.delete("session_id");
    const newQuery = params.toString();
    const newUrl = `${location.pathname}${newQuery ? `?${newQuery}` : ""}${location.hash}`;
    window.history.replaceState({}, "", newUrl);
  }, [location.hash, location.pathname, location.search]);

  const openDonationModal = () => {
    if (!featureFlags.donations) return;
    setDonationSuccessAmount(null);
    setDonationModalShowing(true);
  };

  const closeDonationModal = () => {
    setDonationModalShowing(false);
    setDonationSuccessAmount(null);
  };

  const gameRoutes = [
    "/motion-mapper",
    "/kinematics-lab",
    "/energy",
    "/polarity-shift",
    "/crack-the-circuit",
    "/wavemaker",
    "/magnetism-mini-golf",
    "/amped-up",
    "/amped-up-arena",
    "/visual-accelerometer",
    "/plot-the-path",
  ];
  const isGameRoute = gameRoutes.some((route) => routeMatches(route, { end: false }));
  const shouldOffsetForNav = !isFullscreenRoute && !isGameRoute;

  return (
    <>
      {/* SEO: hreflang alternate links, canonical URL, and dynamic lang attribute */}
      <LocaleSEO />

      {isFullscreenRoute && (
        <Helmet>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
          />
        </Helmet>
      )}
      {!isFullscreenRoute && (
        <SiteNav
          openDonationModal={openDonationModal}
          closeDonationModal={closeDonationModal}
          disableStickyNav={isGameRoute}
        />
      )}
      <div className={shouldOffsetForNav ? styles.AppContent : ""}>
        <Routes>
          {/* Main localized routes - paths are relative to /:locale */}
          <Route
            index
            element={
              <LandingPage
                openDonationModal={openDonationModal}
                closeDonationModal={closeDonationModal}
              />
            }
          />
          {featureFlags.shop && <Route path="shop" element={<StorePage />} />}
          {featureFlags.shop && <Route path="account" element={<AccountPage />} />}
          <Route path="video-vault" element={<VideoVaultPage />} />
          <Route
            path="about"
            element={<AboutPage openDonationModal={openDonationModal} />}
          />
          {featureFlags.games.ampedUp && <Route path="amped-up" element={<AmpedUpGamePage />} />}
          {featureFlags.games.ampedUp && (
            <Route
              path="amped-up-arena"
              element={<AmpedUpRedirect />}
            />
          )}
          <Route path="energy" element={<EnergyBarChartPage />} />
          <Route path="polarity-shift" element={<PolarityShiftPage />} />
          <Route path="motion-mapper" element={<MotionMapperPage />} />
          <Route path="crack-the-circuit" element={<CrackTheCircuitPage />} />
          <Route path="wavemaker" element={<WavemakerPage />} />
          <Route path="visual-accelerometer" element={<VisualAccelerometerPage />} />
          {featureFlags.games.kinematicsLab && (
            <Route path="kinematics-lab" element={<KinematicsLabPage />} />
          )}
          {featureFlags.games.magnetismMiniGolf && (
            <Route path="magnetism-mini-golf" element={<MagnetismMiniGolfPage />} />
          )}
          {featureFlags.games.plotThePath && (
            <Route path="plot-the-path" element={<PlotThePathPage />} />
          )}
          {/* Catch-all redirects to home (respecting locale prefix) */}
          <Route path="*" element={<Navigate to={localePath("/")} replace />} />
        </Routes>
        {!isFullscreenRoute && <Footer />}
      </div>
      {featureFlags.donations && !isFullscreenRoute && (
        <DonationModal
          show={donationModalShowing}
          closeDonationModal={closeDonationModal}
          successAmount={donationSuccessAmount}
        />
      )}
      {featureFlags.shop && !isFullscreenRoute && (
        <AuthModal show={authModalShowing} closeAuthModal={closeAuthModal} authEvent={authEvent} clearAuthEvent={clearAuthEvent} />
      )}
    </>
  );
};

export default App;
